// src/components/ui/AghrbaStyledComponents.jsx
import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Paper,
  Button,
  IconButton,
  TextField,
  Stack,
  styled,
  alpha,
  useTheme
} from '@mui/material';
import { Link } from 'react-router-dom';
import useAghrbaStyles from '../../hooks/useAghrbaStyles';
import { useThemeTokens } from '../../theme/themeHooks';
import TabPanel from './TabPanel';
import { BaseCard as ImportedBaseCard } from '../core/cards';

/**
 * Collection of common styled components that implement the Aghrba theme
 * Use these components instead of applying the same styles repeatedly
 */

// ===== LAYOUT COMPONENTS =====

/**
 * Box with flex layout and items centered vertically
 */
export const FlexBox = styled(Box)(({ theme }) => ({
  ...theme.aghrba.tokens.layout.flex.row,
}));

/**
 * Box with flex column layout
 */
export const FlexColumn = styled(Box)(({ theme }) => ({
  ...theme.aghrba.tokens.layout.flex.column,
}));

/**
 * Box with space-between layout
 */
export const FlexBetween = styled(Box)(({ theme }) => ({
  ...theme.aghrba.tokens.layout.flex.between,
}));

/**
 * Box with centered content
 */
export const FlexCenter = styled(Box)(({ theme }) => ({
  ...theme.aghrba.tokens.layout.flex.center,
}));

/**
 * Box with content right-aligned
 */
export const FlexEnd = styled(Box)(({ theme }) => ({
  ...theme.aghrba.tokens.layout.flex.end,
}));

/**
 * Container for a section with proper spacing
 */
export const SectionContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(theme.aghrba.tokens.layout.spacing.section),
  width: '100%',
}));

/**
 * Container for a subsection with proper spacing
 */
export const SubsectionContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(theme.aghrba.tokens.layout.spacing.subsection),
  width: '100%',
}));

/**
 * Divider with gradient styling
 */
export const GradientDivider = styled(Box)(({ theme }) => ({
  ...theme.aghrba.tokens.borders.gradient(theme),
  height: 1,
  width: '100%',
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

/**
 * Section divider with gradient (from StyledComponents.jsx)
 */
export const SectionDivider = styled(Box)(({ theme }) => {
  const tokens = theme.aghrba.tokens;
  return {
    position: 'relative',
    width: '100%',
    height: 1,
    marginBottom: tokens?.layout?.spacing?.section || theme.spacing(3),
    marginTop: tokens?.layout?.spacing?.section || theme.spacing(3),
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: 1,
      background: tokens?.dividers?.gradient || 
        `linear-gradient(90deg, 
          ${alpha(theme.palette.divider, 0)} 0%, 
          ${alpha(theme.palette.divider, 0.7)} 50%, 
          ${alpha(theme.palette.divider, 0)} 100%
        )`,
      transition: tokens?.transitions?.standard || 'all 0.3s ease',
    },
  };
});

/**
 * Page content container
 */
export const PageContainer = styled(Box)(({ theme }) => ({
  ...theme.aghrba.tokens.layout.container.page,
}));

// ===== CARD COMPONENTS =====

/**
 * Enhanced Card component with interactive hover effect
 * This is a wrapper around the consolidated BaseCard component from core/cards
 */
export const EntityCard = styled(ImportedBaseCard)(({ theme }) => ({
  // Any additional styles needed for backward compatibility
}));

/**
 * CardContent with proper spacing
 */
export const EntityCardContent = styled(CardContent)(({ theme }) => ({
  ...theme.aghrba.tokens.cards.container,
}));

/**
 * Card with subtle shadow and border
 */
export const SubtleCard = styled(Card)(({ theme }) => ({
  ...theme.aghrba.tokens.cards.root(theme),
  boxShadow: 'none',
  borderRadius: theme?.aghrba?.tokens?.borderRadius?.card || 16,
  border: `1px solid ${alpha(theme.palette.divider, theme?.aghrba?.tokens?.alpha?.border || 0.1)}`,
  padding: theme?.aghrba?.tokens?.padding?.card?.medium || '24px',
  transition: theme?.aghrba?.tokens?.transitions?.standard || 'all 0.3s ease',
  '&:hover': {
    boxShadow: theme.shadows[1],
    transform: 'translateY(-2px)',
  },
}));

/**
 * Paper with subtle elevation
 */
export const SubtlePaper = styled(Paper)(({ theme }) => ({
  ...theme.aghrba.tokens.backgrounds.paper(theme),
  padding: theme?.aghrba?.tokens?.padding?.card?.medium || '24px',
  borderRadius: theme?.aghrba?.tokens?.borderRadius?.medium || 8,
  boxShadow: 'none',
  border: `1px solid ${alpha(theme.palette.divider, theme?.aghrba?.tokens?.alpha?.border || 0.1)}`,
  transition: theme?.aghrba?.tokens?.transitions?.standard || 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
  },
}));

/**
 * Standard Paper card with consistent styling
 */
export const PaperCard = styled(Paper)(({ theme, elevation = 0 }) => ({
  borderRadius: theme?.aghrba?.tokens?.borderRadius?.card || 16,
  boxShadow: elevation ? theme.shadows[elevation] : 'none',
  border: `1px solid ${alpha(theme.palette.divider, theme?.aghrba?.tokens?.alpha?.border || 0.1)}`,
  overflow: 'hidden',
  padding: theme?.aghrba?.tokens?.padding?.card?.medium || '24px',
  transition: theme?.aghrba?.tokens?.transitions?.standard || 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[elevation + 1 || 1],
  },
}));

/**
 * Content Paper with backdrop filter (from StyledComponents.jsx)
 */
export const ContentPaper = styled(Paper)(({ theme }) => {
  const tokens = theme.aghrba.tokens;
  return {
    padding: tokens?.padding?.card?.medium || theme.spacing(3),
    borderRadius: tokens?.borderRadius?.card || theme.shape.borderRadius * 1.5,
    boxShadow: theme.shadows[1],
    backdropFilter: 'blur(10px)',
    background: alpha(theme.palette.background.paper, tokens?.alpha?.paperBackground || 0.8),
    transition: tokens?.transitions?.standard || 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[2],
    }
  };
});

// ===== TYPOGRAPHY COMPONENTS =====

/**
 * Text that is truncated to a single line with ellipsis
 * Enhanced with proper contrast for both light and dark themes
 */
export const TruncatedText = styled(Typography)(({ theme, lines = 1, color }) => {
  // Determine high-contrast color based on theme mode and input color
  const getContrastColor = () => {
    // If custom color is supplied, use it
    if (color && color !== 'inherit' && color !== 'textPrimary' && color !== 'textSecondary') {
      return undefined; // Let MUI handle it
    }

    // Apply high contrast color based on theme mode
    if (color === 'textSecondary') {
      return theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.7)
        : alpha(theme.palette.common.black, 0.7);
    }

    // Default text color with enhanced contrast
    return theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.95)
      : alpha(theme.palette.common.black, 0.95);
  };

  return {
    ...(lines === 1
      ? theme.aghrba.tokens.text.truncate
      : theme.aghrba.tokens.text.lineClamp(lines)),
    color: getContrastColor(),
    fontWeight: color === 'textSecondary' ? 'normal' : 500,
  };
});

/**
 * Section heading with proper spacing and weight
 * Enhanced with better contrast for both themes
 */
export const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  fontWeight: theme.aghrba.tokens.text.weight.semibold,
  marginBottom: theme.spacing(2),
  color: theme.palette.mode === 'dark'
    ? alpha(theme.palette.common.white, 0.98)
    : alpha(theme.palette.common.black, 0.98),
  letterSpacing: '-0.01em',
}));

/**
 * Subsection heading with enhanced contrast
 */
export const SubsectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.2rem',
  fontWeight: theme.aghrba.tokens.text.weight.semibold,
  marginBottom: theme.spacing(1.5),
  color: theme.palette.mode === 'dark'
    ? alpha(theme.palette.common.white, 0.95)
    : alpha(theme.palette.common.black, 0.95),
  letterSpacing: '-0.01em',
}));

// ===== UI COMPONENTS =====

/**
 * Status chip with consistent styling
 */
export const StatusChip = ({ 
  status, 
  entityType, 
  label, 
  color = 'default',
  size = 'small',
  ...props 
}) => {
  const styles = useAghrbaStyles();
  
  // Get correct status configuration based on entity type
  const getStatusConfig = () => {
    const statusConfig = {
      client: {
        active: { color: 'success', label: 'Active' },
        inactive: { color: 'error', label: 'Inactive' },
        onboarding: { color: 'warning', label: 'Onboarding' },
        paused: { color: 'info', label: 'Paused' },
        former: { color: 'default', label: 'Former' },
      },
      brand: {
        active: { color: 'success', label: 'Active' },
        inactive: { color: 'error', label: 'Inactive' },
        draft: { color: 'default', label: 'Draft' },
      },
      task: {
        to_do: { color: 'default', label: 'To Do' },
        in_progress: { color: 'info', label: 'In Progress' },
        in_review: { color: 'warning', label: 'In Review' },
        done: { color: 'success', label: 'Done' },
        cancelled: { color: 'error', label: 'Cancelled' },
      },
      project: {
        draft: { color: 'default', label: 'Draft' },
        planning: { color: 'info', label: 'Planning' },
        active: { color: 'success', label: 'Active' },
        on_hold: { color: 'warning', label: 'On Hold' },
        completed: { color: 'success', label: 'Completed' },
        cancelled: { color: 'error', label: 'Cancelled' },
      },
      content: {
        draft: { color: 'default', label: 'Draft' },
        review: { color: 'warning', label: 'Review' },
        approved: { color: 'success', label: 'Approved' },
        published: { color: 'info', label: 'Published' },
        rejected: { color: 'error', label: 'Rejected' },
      }
    };
    
    if (status && entityType && statusConfig[entityType]) {
      return statusConfig[entityType][status] || { color, label: status };
    }
    
    return { color, label: label || status };
  };
  
  const config = getStatusConfig();
  
  return (
    <Chip
      label={config.label}
      color={config.color}
      size={size}
      sx={{
        ...(size === 'small' ? styles.chips.small : {}),
        ...styles.chips.status(config.color),
        ...props.sx,
      }}
      {...props}
    />
  );
};

/**
 * Subtle chip with customizable colors and improved contrast
 */
export const SubtleChip = ({ color = 'default', ...props }) => {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';

  // Map of colors to use for the chip
  const colorMap = {
    default: theme.palette.text.primary,
    primary: theme.palette.primary.main,
    secondary: theme.palette.secondary.main,
    success: theme.palette.success.main,
    error: theme.palette.error.main,
    warning: theme.palette.warning.main,
    info: theme.palette.info.main,
  };

  const selectedColor = colorMap[color] || colorMap.default;

  // Higher opacity for light mode to ensure readability
  const bgOpacity = isLight ? 0.12 : 0.08;
  const borderOpacity = isLight ? 0.3 : 0.2;

  const sx = {
    backgroundColor: alpha(selectedColor, bgOpacity),
    color: selectedColor,
    border: `1px solid ${alpha(selectedColor, borderOpacity)}`,
    fontWeight: 500,
    ...theme.aghrba.tokens.chips.sizes.small,
    ...(props.sx || {}),
  };

  return <Chip {...props} sx={sx} />;
};

/**
 * Search field with consistent styling
 */
export const SearchField = styled(TextField)(({ theme }) => {
  const tokens = theme.aghrba.tokens;
  return {
    '& .MuiOutlinedInput-root': {
      backgroundColor: alpha(theme.palette.background.paper, tokens?.alpha?.inputBackground || 0.6),
      backdropFilter: 'blur(8px)',
      borderRadius: tokens?.borderRadius?.input || 30,
      height: tokens?.buttonHeight?.small || 48.5,
      transition: tokens?.transitions?.standard || 'all 0.3s ease',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.secondary.main,
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.secondary.main,
        boxShadow: `0 0 0 3px ${alpha(theme.palette.secondary.main, 0.2)}`,
      },
    },
    '& .MuiInputBase-input': {
      padding: tokens?.padding?.input?.small || '14px 20px',
    },
  };
});

/**
 * Action button with rounded styling
 */
export const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme?.aghrba?.tokens?.borderRadius?.button || 30,
  textTransform: 'none',
  padding: theme?.aghrba?.tokens?.padding?.button?.small || '14px 20px',
  height: theme?.aghrba?.tokens?.buttonHeight?.small || 48.5,
  transition: theme?.aghrba?.tokens?.transitions?.standard || 'all 0.3s ease',
}));

/**
 * Link text that can be used with react-router Link
 */
export const LinkText = styled(Typography)(({ theme, noUnderline }) => ({
  color: theme.palette.secondary.main,
  textDecoration: noUnderline ? 'none' : 'underline',
  cursor: 'pointer',
  '&:hover': {
    color: theme.palette.secondary.light,
  },
}));

/**
 * Customized link for navigation
 */
export const RouterLink = ({ to, children, ...props }) => {
  const styles = useAghrbaStyles();
  
  return (
    <Typography
      component={Link}
      to={to}
      sx={{
        color: 'secondary.main',
        textDecoration: 'none',
        '&:hover': {
          color: 'secondary.light',
          textDecoration: 'underline',
        },
        ...props.sx,
      }}
      {...props}
    >
      {children}
    </Typography>
  );
};

/**
 * Wrapper for form sections
 */
export const FormSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

/**
 * Form field row with proper spacing
 */
export const FormRow = styled(Box)(({ theme, columns = 2 }) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(${columns}, 1fr)`,
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
  [theme.breakpoints?.down('sm') || '@media (max-width: 600px)']: {
    gridTemplateColumns: '1fr',
  },
}));

/**
 * Task Card styled specifically for tasks
 */
export const TaskCard = styled(Paper)(({
  theme,
  priority,
  isDragging = false,
  isOverdue = false,
  isDone = false,
  interactive = true
}) => {
  // Determine border color based on priority or overdue status
  let borderColor = 'transparent';
  if (isOverdue) {
    borderColor = theme.palette.error.main;
  } else if (priority === 'urgent') {
    borderColor = theme.palette.error.main;
  } else if (priority === 'high') {
    borderColor = theme.palette.warning.main;
  }

  return {
    borderRadius: theme?.aghrba?.tokens?.borderRadius?.card || 16,
    padding: theme?.aghrba?.tokens?.padding?.card?.small || '16px',
    backgroundColor: isDragging
      ? theme.palette.background.default
      : theme.palette.background.paper,
    borderLeft: borderColor !== 'transparent'
      ? `4px solid ${borderColor}`
      : `1px solid ${isDragging ? theme.palette.primary.main : theme.palette.divider}`,
    border: `1px solid ${isDragging ? theme.palette.primary.main : theme.palette.divider}`,
    transition: theme?.aghrba?.tokens?.transitions?.standard || 'all 0.3s ease',
    ...(interactive && {
      cursor: 'pointer',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[2],
        '.MuiIconButton-root': { opacity: 1 }
      }
    }),
    ...(isDone && {
      opacity: 0.8,
    }),
    // Add additional styling for focus state
    '&:focus-within': {
      boxShadow: `0 0 0 2px ${theme.palette.mode === 'dark' 
        ? theme.palette.secondary.main 
        : theme.palette.primary.main}`,
      outline: 'none',
    }
  };
});

/**
 * Enhanced Tab Panel with proper theme-aware styling
 * Re-exports the TabPanel component from our enhanced version
 */
export { TabPanel };

/**
 * Panel component for tabs content with consistent styling
 */
export const ContentPanel = styled(Box)(({ theme }) => ({
  padding: theme?.aghrba?.tokens?.tabs?.panel?.padding || '1.5rem 0',
  backgroundColor: 'transparent',
  position: 'relative',
  color: theme.palette.mode === 'dark'
    ? alpha(theme.palette.common.white, 0.95)
    : alpha(theme.palette.common.black, 0.95),
  transition: theme?.aghrba?.tokens?.transitions?.standard || 'all 0.3s ease',
  borderRadius: theme?.aghrba?.tokens?.borderRadius?.medium || 8,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: theme.palette.mode === 'dark'
      ? alpha(theme.palette.background.paper, 0.05)
      : alpha(theme.palette.background.paper, 0.02),
    borderRadius: 'inherit',
    zIndex: -1,
  },
  '& .MuiTypography-root': {
    color: 'inherit',
    marginBottom: theme.spacing(2),
  },
  '& .MuiFormControl-root': {
    marginBottom: theme.spacing(2.5),
  }
}));

// ===== DEMO COMPONENT =====

/**
 * Demo component showing usage examples (from StyledComponents.jsx)
 */
export const StyledComponentsDemo = () => {
  const theme = useTheme();
  
  return (
    <PageContainer>
      <SectionTitle>Styled Components Demo</SectionTitle>
      
      <SectionContainer>
        <SubsectionTitle>Layout Components</SubsectionTitle>
        <ContentPaper>
          <FlexBetween sx={{ mb: 2 }}>
            <Typography variant="body1">FlexBetween Component</Typography>
            <StatusChip status="active" entityType="client" />
          </FlexBetween>
          
          <FlexColumn sx={{ gap: 1, mb: 2 }}>
            <Typography variant="body1">FlexColumn Component</Typography>
            <TruncatedText lines={2}>
              This text will be truncated after two lines. This text will be truncated after two lines.
              This text will be truncated after two lines. This text will be truncated after two lines.
            </TruncatedText>
          </FlexColumn>
        </ContentPaper>
      </SectionContainer>
      
      <SectionDivider />
      
      <SectionContainer>
        <SubsectionTitle>Card Components</SubsectionTitle>
        <EntityCard borderColor={theme.palette.primary.main}>
          <EntityCardContent>
            <TruncatedText variant="h6" gutterBottom>Entity Card Title</TruncatedText>
            <TruncatedText variant="body2" color="textSecondary" lines={2}>
              This is an entity card with consistent styling. All card components should
              use this pattern to ensure visual consistency across the application.
            </TruncatedText>
          </EntityCardContent>
        </EntityCard>
      </SectionContainer>
    </PageContainer>
  );
};

// Export all components as default object
export default {
  FlexBox,
  FlexColumn,
  FlexBetween,
  FlexCenter,
  FlexEnd,
  SectionContainer,
  SubsectionContainer,
  GradientDivider,
  SectionDivider,
  PageContainer,
  EntityCard,
  EntityCardContent,
  SubtleCard,
  SubtlePaper,
  PaperCard,
  ContentPaper,
  TaskCard,
  TruncatedText,
  SectionTitle,
  SubsectionTitle,
  StatusChip,
  SubtleChip,
  SearchField,
  ActionButton,
  LinkText,
  RouterLink,
  FormSection,
  FormRow,
  ContentPanel,
  TabPanel,
  StyledComponentsDemo,
};